/**
 * Auth API (RTK Query) — Stride admin v2 endpoints only.
 */

import { createApi } from '@reduxjs/toolkit/query/react'
import { storeAuthData } from '@/core/auth'
import { User, LoginCredentials, RegisterCredentials } from '@/types'
import {
  mapAdminProfileToUser,
  normalizeAdminLoginResponse,
} from '@/services/adminResponseMappers'
import { strideBaseQuery } from '@/services/rtkBaseQuery'

interface LoginResponse {
  user: User
  token: string
  expiresIn: number
}

interface RegisterResponse {
  user: User
  token: string
  expiresIn: number
}

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: strideBaseQuery,
  tagTypes: ['User'],
  endpoints: builder => ({
    login: builder.mutation<LoginResponse, LoginCredentials>({
      query: credentials => ({
        url: '/admin/auth/login',
        method: 'POST',
        body: {
          email: credentials.email,
          password: credentials.password,
        },
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeAdminLoginResponse(response)
        if (!normalized) {
          throw new Error('Unexpected login response from server.')
        }
        const expiresAt = Date.now() + normalized.expiresIn * 1000
        storeAuthData({
          token: normalized.token,
          user: normalized.user,
          expiresAt,
        })
        return {
          user: normalized.user,
          token: normalized.token,
          expiresIn: normalized.expiresIn,
        }
      },
    }),
    register: builder.mutation<RegisterResponse, RegisterCredentials>({
      queryFn: async () => ({
        error: {
          status: 501,
          data: {
            message:
              'Registration is not available on the admin API. Use admin login instead.',
          },
        },
      }),
    }),
    logout: builder.mutation<void, void>({
      query: () => ({
        url: '/admin/auth/logout',
        method: 'POST',
      }),
    }),
    getCurrentUser: builder.query<User, void>({
      query: () => '/admin/me',
      transformResponse: (response: unknown) => mapAdminProfileToUser(response),
      providesTags: ['User'],
    }),
    refreshToken: builder.mutation<{ token: string; expiresIn: number }, void>({
      queryFn: async () => ({
        error: {
          status: 501,
          data: { message: 'Token refresh is not exposed on the admin API.' },
        },
      }),
    }),
  }),
})

export const {
  useLoginMutation,
  useRegisterMutation,
  useLogoutMutation,
  useGetCurrentUserQuery,
  useRefreshTokenMutation,
} = authApi
